import React, { useState } from 'react';

import { ConfirmationDialog } from '@/components/ConfirmationDialog';
import { Agent } from '@/hooks/useAgents';

import { useTunnelManagement } from './useTunnelManagement';

interface RemoveTunnelModalProps {
  isOpen: boolean;
  onClose: () => void;
  agent: Agent;
  tunnelHostname: string;
  tunnelName?: string;
  onSuccess?: (taskId?: string) => void;
}

/**
 * Modal for removing a Cloudflare tunnel from an Aegis agent.
 *
 * Extracted from TunnelManagementTab to enable direct modal access from row actions
 * following information architecture best practices (colocation, reusability).
 *
 * Can be opened from:
 * - Row action "Remove Tunnel" (direct modal access)
 * - Tunnel tab "Remove Tunnel" button (within drawer)
 */
export const RemoveTunnelModal: React.FC<RemoveTunnelModalProps> = ({
  isOpen,
  onClose,
  agent,
  tunnelHostname,
  tunnelName,
  onSuccess,
}) => {
  const [isRemoving, setIsRemoving] = useState(false);
  const tunnelManagement = useTunnelManagement(isOpen);

  const handleRemove = async () => {
    if (!agent) return;

    try {
      setIsRemoving(true);

      const response = await tunnelManagement.removeTunnel(agent.client_id);

      onClose();
      onSuccess?.(response?.taskId);
    } catch (error) {
      console.error('Failed to remove tunnel:', error);
      // Error handling is managed by the tunnel management hook
    } finally {
      setIsRemoving(false);
    }
  };

  const descriptionParts = [
    `Are you sure you want to remove the tunnel from agent ${agent.hostname}?`,
    tunnelName ? `Tunnel: ${tunnelName}` : '',
    tunnelHostname ? `Hostname: ${tunnelHostname}` : '',
    'This will disable secure SSH access to the agent.',
  ].filter(Boolean);

  return (
    <ConfirmationDialog
      open={isOpen}
      onClose={onClose}
      onConfirm={handleRemove}
      title="Remove Cloudflare Tunnel"
      description={descriptionParts.join(' ')}
      confirmLabel="Remove Tunnel"
      loadingLabel="Removing..."
      isLoading={isRemoving}
      data-testid="remove-tunnel-modal"
    />
  );
};
